import React, { useState, useEffect } from "react";
import { useRef } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Checkbox,
	Button,
	IconButton,
	Box,
	Alert,
	TextField,
	Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import InputAdornment from "@mui/material/InputAdornment";
import Chip from "@mui/material/Chip";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const DAY_NAMES = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
const DAY_MAP = {
	0: "lunes",
	1: "martes",
	2: "miercoles",
	3: "jueves",
	4: "viernes",
	5: "sabado",
	6: "domingo",
};

const toMinutes = (s) => {
	if (!s) return null;
	const [h, m] = s.split(":").map(Number);
	return h * 60 + m;
};

export function GridEdit({ idContract, dataDays = [], dataPeriods = [], onDataUpdate }) {
	const { t } = useTranslation();

	// ref para inicializar solo una vez
	const initializedRef = useRef(false);
	const [grid, setGrid] = useState({});

	// Inicialización única del grid con IDs de grupo conservados
	useEffect(() => {
		if (initializedRef.current) return;
		if (!dataDays.length || !dataPeriods.length) return;

		const initial = {};
		dataDays.forEach((d) => {
			const dn = DAY_MAP[d.day];
			// Busco el grupo completo para obtener su ID
			const grupo = dataPeriods.find(
				(g) => g.config_contracts_per_days_id === d.config_contracts_per_days_id
			);
			const groupId = grupo?.config_contracts_per_periods_id ?? null;

			// Mapear cada periodo, inyectando el mismo ID de grupo
			const periods = (grupo?.periods || []).map((inner) => ({
				id: groupId,
				start: inner.start_time ? inner.start_time.slice(0, 5) : "",
				end: inner.end_time ? inner.end_time.slice(0, 5) : "",
				error: "",
			}));

			initial[dn] = {
				config_contracts_per_days_id: d.config_contracts_per_days_id,
				config_contracts_per_periods_id: groupId,
				selected: d.value === 1,
				periods: periods.length ? periods : [],
			};
		});

		// Asegurar todas las claves del grid
		DAY_NAMES.forEach((dn) => {
			if (!initial[dn]) {
				initial[dn] = {
					config_contracts_per_days_id: null,
					config_contracts_per_periods_id: null,
					selected: false,
					periods: [],
				};
			}
		});

		setGrid(initial);
		initializedRef.current = true;
	}, [dataDays, dataPeriods]);

	const emitUpdate = (gridState) => {
		const daysArr = DAY_NAMES.map((dn, i) => ({
			config_contracts_per_days_id: gridState[dn].config_contracts_per_days_id,
			contract_id: idContract,
			day: i,
			value: gridState[dn].selected ? 1 : 0,
		}));

		const periodsArr = DAY_NAMES.reduce((acc, dn) => {
			if (!gridState[dn].selected) return acc;

			acc.push({
				config_contracts_per_days_id: gridState[dn].config_contracts_per_days_id,
				config_contracts_per_periods_id: gridState[dn].config_contracts_per_periods_id,
				periods: gridState[dn].periods.map((p) => ({
					start_time: p.start,
					end_time: p.end,
				})),
			});

			return acc;
		}, []);

		onDataUpdate(daysArr, periodsArr);
	};

	const toggleDay = (day) =>
		setGrid((g) => {
			const sel = !g[day].selected;
			const next = {
				...g,
				[day]: {
					...g[day],
					selected: sel,
					periods: sel
						? g[day].periods.length
							? g[day].periods
							: [{ id: null, start: "", end: "", error: "" }]
						: [],
				},
			};
			emitUpdate(next);
			return next;
		});

	const addPeriod = (day) =>
		setGrid((g) => {
			const next = {
				...g,
				[day]: {
					...g[day],
					periods: [...g[day].periods, { start: "", end: "", error: "" }],
				},
			};
			emitUpdate(next);
			return next;
		});

	const removePeriod = (day, idx) =>
		setGrid((g) => {
			const nextPeriods = g[day].periods.filter((_, i) => i !== idx);
			const next = { ...g, [day]: { ...g[day], periods: nextPeriods } };
			emitUpdate(next);
			return next;
		});

	const onTimeChange = (day, idx, field, newVal) => {
		const formatted = newVal ? dayjs(newVal).format("HH:mm") : "";
		setGrid((g) => {
			const nextPeriods = g[day].periods.map((p, i) => (i === idx ? { ...p, [field]: formatted } : p));
			// validar error
			let err = "";
			const { start, end } = nextPeriods[idx];
			if (start && end && toMinutes(end) < toMinutes(start)) {
				err = t("aux.grid_d_p.error_format");
			}
			nextPeriods[idx].error = err;
			const next = { ...g, [day]: { ...g[day], periods: nextPeriods } };
			emitUpdate(next);
			return next;
		});
	};

	const allEmpty = DAY_NAMES.every((dn) => !grid[dn]?.selected);
	const hasEmptyPair = DAY_NAMES.some(
		(dn) => grid[dn]?.selected && grid[dn].periods.some((p) => !p.start || !p.end)
	);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<TableContainer sx={{ width: "100%", overflowX: "hidden" }}>
				<Table size="small" sx={{ minWidth: 0 }}>
					<TableHead>
						<TableRow>
							<TableCell>{t("aux.day")}</TableCell>
							<TableCell>{t("aux.attend")}</TableCell>
							<TableCell>{t("aux.period")}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{DAY_NAMES.map((dn, idx) => (
							<TableRow key={dn}>
								<TableCell>{t(`aux.days.${dn}`)}</TableCell>
								<TableCell>
									<Checkbox checked={grid[dn]?.selected || false} onChange={() => toggleDay(dn)} />
								</TableCell>
								<TableCell>
									{grid[dn]?.selected &&
										grid[dn].periods.map((p, i) => (
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													mb: 1,
													flexWrap: "wrap",
													gap: 1,
													minWidth: 220,
													width: "100%",
												}}
											>
												<TimePicker
													label={t("aux.start_time")}
													value={p.start ? dayjs(p.start, "HH:mm", true) : null}
													onChange={(v) => onTimeChange(dn, i, "start", v)}
													ampm={false}
													format="HH:mm"
													mask="__:__"
													views={["hours", "minutes"]} // sin segundos
													timeSteps={{ minutes: 60 }}
													enableAccessibleFieldDOMStructure={false}
													slots={{ textField: TextField }}
													slotProps={{
														textField: {
															size: "small",
															fullWidth: true,
															sx: { m: 1, flex: 1, minWidth: 140 },
															InputProps: {
																sx: {
																	padding: "3px",
																	"& .MuiInputBase-input": {
																		fontSize: "1rem",
																		padding: "3px 5px",
																	},
																},
															},
															inputProps: {
																style: {
																	fontSize: "1rem",
																	padding: "6px 2px",
																},
																readOnly: true,
																onPaste: (e) => e.preventDefault(),
																onKeyDown: (e) => {
																	const ok = ["Tab", "Shift", "ArrowLeft", "ArrowRight", "Home", "End"];
																	if (!ok.includes(e.key)) e.preventDefault();
																},
															},
														},
													}}
												/>
												<TimePicker
													label={t("aux.end_time")}
													value={p.end ? dayjs(p.end, "HH:mm", true) : null}
													onChange={(v) => onTimeChange(dn, i, "end", v)}
													ampm={false}
													format="HH:mm"
													mask="__:__"
													views={["hours", "minutes"]}
													timeSteps={{ minutes: 60 }}
													enableAccessibleFieldDOMStructure={false}
													slots={{
														textField: (params) => (
															<TextField
																{...params}
																size="small"
																sx={{ m: 1, flex: 1, minWidth: 140 }}
																fullWidth
																InputProps={{
																	...params.InputProps,
																	endAdornment: (
																		<>
																			{params.InputProps?.endAdornment}
																			<InputAdornment position="end" sx={{ ml: 0.5 }}>
																				<Chip
																					label="23:59"
																					size="small"
																					variant="outlined"
																					onMouseDown={(e) => e.preventDefault()}
																					onClick={() => onTimeChange(dn, i, "end", dayjs("23:59", "HH:mm"))}
																				/>
																			</InputAdornment>
																		</>
																	),
																}}
																inputProps={{
																	...params.inputProps,
																	readOnly: true,
																	onPaste: (e) => e.preventDefault(),
																	onKeyDown: (e) => {
																		const ok = ["Tab", "Shift", "ArrowLeft", "ArrowRight", "Home", "End"];
																		if (!ok.includes(e.key)) e.preventDefault();
																	},
																}}
															/>
														),
													}}
													slotProps={{
														textField: {
															size: "small",
															sx: {
																m: 1,
																width: "100px",
																minWidth: "80px",
															},
															InputProps: {
																sx: {
																	padding: "3px",
																	"& .MuiInputBase-input": {
																		fontSize: "1rem",
																		padding: "3px 5px",
																	},
																},
															},
															inputProps: {
																style: {
																	fontSize: "1rem",
																	padding: "6px 2px",
																},
															},
														},
													}}
												/>
												<IconButton size="small" onClick={() => removePeriod(dn, i)}>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</Box>
										))}
									{grid[dn]?.selected && grid[dn].periods.length === 0 && (
										<Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
											Debe deseleccionar o agregar al menos un par de horas
										</Typography>
									)}
									{grid[dn]?.selected && (
										<Button size="small" onClick={() => addPeriod(dn)}>
											{t("aux.buttons.more")}
										</Button>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			{allEmpty && (
				<Alert severity="error" sx={{ mt: 2 }}>
					{t("aux.messages.alert.grid_d_p.error")}
				</Alert>
			)}
			{!allEmpty && hasEmptyPair && (
				<Alert severity="error" sx={{ mt: 2 }}>
					{t("aux.messages.alert.grid_d_p.pairs_error")}
				</Alert>
			)}
		</LocalizationProvider>
	);
}
